import type {
	ChatContentBlockDelta,
	ChatMessage,
	ChatOutputContentBlock,
	ChatOutputMessage,
	ChatStreamChunk,
	ToolUseContentBlock,
} from '#layers/ai/types/chat';
import { parse } from 'partial-json';

/**
 * Applies a single streamed chat event to the current transcript.
 *
 * Partial assistant updates replace the previous `messages` reference so callers can
 * store it in a `shallowRef` and still react while a message is streaming.
 */
export function applyChatStreamEvent(
	messages: ChatMessage[],
	streamEvent: ChatStreamChunk,
): ChatMessage[] {
	if (streamEvent === '[DONE]') return messages;

	switch (streamEvent.type) {
		case 'messageStart':
			return [
				...messages,
				{
					role: 'assistant',
					content: [],
				},
			];
		case 'contentBlockStart':
			return replaceLastStreamMessage(messages, (message) => ({
				...message,
				content: replaceContentBlock(
					message.content,
					streamEvent.blockId,
					streamEvent.contentBlock,
				),
			}));
		case 'contentBlockDelta':
			return replaceLastStreamMessage(messages, (message) => ({
				...message,
				content: updateContentBlock(
					message.content,
					streamEvent.blockId,
					(block) => applyContentBlockDelta(block, streamEvent.delta),
				),
			}));
		case 'contentBlockStop':
			return replaceLastStreamMessage(messages, (message) => ({
				...message,
				content: replaceContentBlock(
					message.content,
					streamEvent.blockId,
					streamEvent.contentBlock,
				),
			}));
		case 'messageStop':
		case 'iterationLimitReached':
			return messages;
		case 'error':
			throw new Error(streamEvent.error);
	}
}

function replaceLastStreamMessage(
	messages: ChatMessage[],
	update: (message: ChatOutputMessage) => ChatOutputMessage,
): ChatMessage[] {
	const lastMessageIndex = messages.length - 1;

	if (lastMessageIndex < 0) {
		return messages;
	}

	const nextMessages = [...messages];
	nextMessages[lastMessageIndex] = update(messages[lastMessageIndex] as ChatOutputMessage);
	return nextMessages;
}

function replaceContentBlock(
	content: ChatOutputMessage['content'],
	blockId: number,
	block: ChatOutputContentBlock,
) {
	const nextContent = [...content];
	nextContent[blockId] = block;
	return nextContent;
}

function updateContentBlock(
	content: ChatOutputMessage['content'],
	blockId: number,
	update: (block: ChatOutputContentBlock) => ChatOutputContentBlock,
) {
	const block = content[blockId];

	if (!block) return content;

	const nextBlock = update(block);

	if (nextBlock === block) return content;

	const nextContent = [...content];
	nextContent[blockId] = nextBlock;
	return nextContent;
}

/**
 * Merges an incremental block delta into the current in-progress assistant block.
 *
 * Tool-call arguments keep accumulating in the client-only `partialInputText` field,
 * while `input` is updated with the best-effort parsed JSON preview for richer UI rendering.
 */
function applyContentBlockDelta(
	block: ChatOutputContentBlock,
	delta: ChatContentBlockDelta,
): ChatOutputContentBlock {
	switch (delta.type) {
		case 'textDelta':
			if (block.type !== 'text') return block;

			return {
				...block,
				text: block.text + delta.text,
			};
		case 'thinkingDelta':
			if (block.type !== 'thinking') return block;

			return {
				...block,
				thinking: block.thinking + delta.thinking,
			};
		case 'inputJsonDelta': {
			if (block.type !== 'toolUse') return block;

			const partialInputText = (block.partialInputText ?? '') + delta.partialJson;

			return {
				...block,
				input: parsePartialToolUseInput(partialInputText, block.input),
				partialInputText,
			};
		}
	}
}

function parsePartialToolUseInput(
	partialInputText: string,
	fallback: ToolUseContentBlock['input'],
): ToolUseContentBlock['input'] {
	try {
		return parse(partialInputText) as ToolUseContentBlock['input'];
	}
	catch {
		return fallback;
	}
}
